import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSortBy, useTable } from 'react-table';
import { useTranslation } from 'react-i18next';

import useRedirect from 'utils/hooks/useRedirect';
import useUsersTableColumns from './useUsersTableColumns';
import useSelectedInstitution from 'utils/hooks/institutions/useSelectedInstitution';
import useIsMainUserOfInstitution from 'utils/hooks/auth/useIsMainUserOfInstitution';
import useCurrentLanguage from 'utils/hooks/useCurrentLanguage';

import { toggleUserFormModal } from 'store/entities/users';
import { getInstitutionUsersList } from 'store/entities/users/thunks';

import { mdiArrowDown, mdiArrowUp, mdiArrowUpDown } from '@mdi/js';
import Icon from '@mdi/react';
import { Link, Redirect } from 'react-router-dom';

import UserFormModal from './UserFormModal';
import MainUserModal from './MainUserModal';
import DeleteUserConfirmationModal from './DeleteUserConfirmationModal';

import Breadcrumb from 'react-bulma-components/lib/components/breadcrumb';
import Button, { ButtonGroup } from 'components/UIComponents/Button';
import { Column, Section } from 'components/UIComponents/Layout';
import { Title } from 'components/UIComponents/Typography';
import Table, { EmptyTable } from 'components/UIComponents/Table';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';
import ChangeMainUser from './ChangeMainUser';

export default () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const redirect = useRedirect();
  const language = useCurrentLanguage();

  const { institutionId, institutionName } = useSelectedInstitution();
  const isMainUserOfInstitution = useIsMainUserOfInstitution();

  const { users, usersLoading } = useSelector(state => state.users);

  const data = useMemo(() => users, [users]);
  const columns = useUsersTableColumns();

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  useEffect(() => {
    dispatch(getInstitutionUsersList(institutionId));
  }, [dispatch, institutionId]);

  const BreadcrumbItem = ({ href, children }) => {
    const currentLang = useCurrentLanguage();

    return <Link to={`/${currentLang}${href}`}>{children}</Link>;
  };

  return (
    <div>
      {!isMainUserOfInstitution && <Redirect to={`/${language}`}></Redirect>}
      <UserFormModal />
      <MainUserModal />
      <DeleteUserConfirmationModal />
      <Section size="medium">
        <Breadcrumb
          renderAs={BreadcrumbItem}
          hrefAttr="href"
          items={[
            {
              name: institutionName,
              url: `/${institutionId}/submissions`,
            },
            {
              name: t('datagate.external.users.title'),
              url: `/${institutionId}/reporters`,
            },
          ]}
        />
        <Title
          id="users-title"
          aria-label="Users view title"
          title={t('datagate.external.users.title')}
        >
          <ButtonGroup>
            <Button
              id="users-add-btn"
              aria-label="Users add button"
              color="primary"
              onClick={() => dispatch(toggleUserFormModal({ action: 'ADD' }))}
            >
              {t('datagate.external.users.button.add')}
            </Button>
            <Button
              id="users-close-btn"
              aria-label="Users view close button"
              color="primary"
              outlined
              onClick={() => redirect('')}
            >
              &#215; {t('datagate.external.users.button.close')}
            </Button>
          </ButtonGroup>
        </Title>
        <Column className="table-wrap">
          <LoadingSpinner loading={usersLoading} />
          <Table {...getTableProps()} striped={false}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={column.isSorted ? 'sorted' : ''}
                    >
                      <div>
                        <abbr title={column.render('Header')} className="mr-5">
                          {column.render('Header')}
                        </abbr>

                        {!column.disableSortBy && !usersLoading && (
                          <span>
                            {' '}
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon path={mdiArrowDown} size={0.7} />
                              ) : (
                                <Icon path={mdiArrowUp} size={0.7} />
                              )
                            ) : (
                              <Icon path={mdiArrowUpDown} size={0.7} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="users-table" {...getTableBodyProps()}>
              <ChangeMainUser />
              {rows.length === 0 && <EmptyTable />}
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Column>
      </Section>
    </div>
  );
};
